import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AssistantTabForm, { TabSection } from './AssistantTabForm.tsx';
import ToggleCard from './ToggleCard.tsx';
import { useAssistantConfigForm } from './useAssistantConfigForm.ts';
import Alert from '../../../../core/components/Alert.tsx';
import DropArea from '../../../../core/components/DropArea.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { useToaster } from '../../../../core/components/Toast.tsx';
import Tooltip from '../../../../core/components/Tooltip.tsx';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';
import { canConfigureKnowledgeSources, canSeeSupportUserConfigs } from '../../../../users/utils.ts';
import { KNOWLEDGE_FILE_ACCEPT, KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL } from '../../constants.ts';
import {
    AGENT_CONFIG_STEPS,
    SFTP_KNOWLEDGE_SOURCE_DEFAULT,
    SHAREPOINT_TOOL_PROVIDER_DEFAULT,
} from '../config/ConfigSteps.tsx';
import InstructionSections from '../knowledge/InstructionSections.tsx';
import KnowledgeFileList from '../knowledge/KnowledgeFileList.tsx';
import { KnowledgeUrlItems, KnowledgeUrlStatus, UrlSyncedFile } from '../knowledge/KnowledgeUrlItems.tsx';
import { SftpKnowledgeSourceCard } from '../knowledge/SftpKnowledgeSourceCard.tsx';
import { SharePointProviderCard } from '../knowledge/SharePointProviderCard.tsx';

const InfoIcon = withIcon(faCircleInfo);

// values set outside a field's own onChange count as changes, so the save status sees them
const DIRTY = { shouldDirty: true };

interface AssistantKnowledgeTabProps {
    agent: any;
    onChange: () => Promise<unknown>;
}

/** The Knowledge tab: synced connections, knowledge files and web pages, expert knowledge and file attachments. Same
 *  fields and rules as the agents config's Knowledge step, plus SharePoint and SFTP from its Integrations step. */
const AssistantKnowledgeTab = ({ agent, onChange }: AssistantKnowledgeTabProps) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);

    // synced sources point at company systems (mirrors the backend's knowledge source permission)
    const canConfigureSources = canConfigureKnowledgeSources(user);
    const isSupportUser = canSeeSupportUserConfigs(user);

    const files = form.watch('files') || [];
    const sharepoint = form.watch('sharepointToolProvider');
    const sftp = form.watch('sftpKnowledgeSource');

    const urlStatuses = new Map<string, KnowledgeUrlStatus>(
        (agent?.knowledgeUrlStatuses || []).map((status: KnowledgeUrlStatus) => [status.url, status])
    );
    const urlSyncedFiles = new Map<string, UrlSyncedFile>(
        (agent?.knowledgeFiles || [])
            .filter((file: any) => file.sourceType === 'url' && file.sourceItemId)
            .map((file: any) => [file.sourceItemId, { fileName: file.fileName, fileUrl: file.fileUrl }])
    );
    const savedUrls = new Set<string>(agent?.knowledgeUrls || []);

    const isSharepointValid =
        !sharepoint ||
        (Boolean(sharepoint.integrationId) && Boolean(sharepoint.driveId) && sharepoint.selectedItems.length > 0);
    const isSftpValid = !sftp || (Boolean(sftp.sftpConfigId) && Boolean(sftp.path));

    const handleDropRejected = (fileRejections: any[]) => {
        publishToast({
            description: tAgentConfig('form.unsupportedFileType', {
                files: fileRejections.map((rejection) => rejection.file.name).join(', '),
                supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL,
            }),
            status: 'error',
        });
    };

    return (
        <AssistantTabForm
            title={t('detail.knowledgeTab.title')}
            description={t('detail.knowledgeTab.description')}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isValid={AGENT_CONFIG_STEPS.knowledge.isValid!(form) && isSharepointValid && isSftpValid}
        >
            {canConfigureSources && (
                <TabSection
                    title={t('detail.knowledgeTab.connections.title')}
                    description={t('detail.knowledgeTab.connections.description')}
                >
                    <Controller
                        name="sharepointToolProvider"
                        control={form.control}
                        render={({ field }) => (
                            <SharePointProviderCard
                                isChecked={field.value !== null}
                                disabled={sftp !== null}
                                onIsCheckedChange={(isChecked) =>
                                    form.setValue(
                                        'sharepointToolProvider',
                                        isChecked ? { ...SHAREPOINT_TOOL_PROVIDER_DEFAULT } : null,
                                        DIRTY
                                    )
                                }
                                config={field.value}
                                onConfigChange={(config) => form.setValue('sharepointToolProvider', config, DIRTY)}
                            />
                        )}
                    />
                    {/* SFTP is set up by Workist support; others only see it once it is configured, read-only */}
                    {(isSupportUser || sftp) && (
                        <Controller
                            name="sftpKnowledgeSource"
                            control={form.control}
                            render={({ field }) => (
                                <SftpKnowledgeSourceCard
                                    isChecked={field.value !== null}
                                    disabled={sharepoint !== null}
                                    readOnly={!isSupportUser}
                                    onIsCheckedChange={(isChecked) =>
                                        form.setValue(
                                            'sftpKnowledgeSource',
                                            isChecked ? { ...SFTP_KNOWLEDGE_SOURCE_DEFAULT } : null,
                                            DIRTY
                                        )
                                    }
                                    config={field.value}
                                    onConfigChange={(config) => form.setValue('sftpKnowledgeSource', config, DIRTY)}
                                />
                            )}
                        />
                    )}
                </TabSection>
            )}

            <TabSection
                title={t('detail.knowledgeTab.files.title')}
                titleAddon={
                    <Tooltip
                        content={t('detail.knowledgeTab.files.supportedTypes', {
                            supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL,
                        })}
                        long
                    >
                        <button
                            type="button"
                            aria-label={t('detail.knowledgeTab.files.supportedTypesLabel')}
                            className="flex rounded text-xs text-content-medium outline-none transition-colors hover:text-content-strong focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                        >
                            <InfoIcon />
                        </button>
                    </Tooltip>
                }
                description={t('detail.knowledgeTab.files.description')}
                descriptionClassName="text-xs"
            >
                <DropArea
                    placeholder={tAgentConfig('form.dropFilesPlaceholder')}
                    placeholderActive={tAgentConfig('form.dropFilesActive')}
                    onFilesChange={(newFiles: File[]) =>
                        form.setValue('files', [...(form.getValues('files') || []), ...newFiles], DIRTY)
                    }
                    onDropRejected={handleDropRejected}
                    accept={KNOWLEDGE_FILE_ACCEPT}
                    files={[]}
                />
                {files.length > 0 && (
                    <KnowledgeFileList
                        files={files}
                        onDelete={(fileToDelete) =>
                            form.setValue(
                                'files',
                                (form.getValues('files') || []).filter((file) => file !== fileToDelete),
                                DIRTY
                            )
                        }
                    />
                )}
                <Controller
                    name="knowledgeUrls"
                    control={form.control}
                    render={({ field }) => (
                        <KnowledgeUrlItems
                            urls={field.value || []}
                            onChange={field.onChange}
                            savedUrls={savedUrls}
                            statuses={urlStatuses}
                            syncedFiles={urlSyncedFiles}
                        />
                    )}
                />
            </TabSection>

            <Controller
                name="instructions"
                control={form.control}
                render={({ field }) => (
                    <InstructionSections
                        label={tAgentConfig('form.instructions')}
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />

            <Controller
                name="attachmentsEnabled"
                control={form.control}
                render={({ field }) => (
                    <ToggleCard
                        title={t('detail.knowledgeTab.attachments.name')}
                        description={t('detail.knowledgeTab.attachments.description')}
                        descriptionClassName="text-xs"
                        isChecked={Boolean(field.value)}
                        onIsCheckedChange={field.onChange}
                    >
                        <Alert severity="info" className="mt-2 text-xs">
                            {t('detail.knowledgeTab.attachments.requiresConsent')}
                        </Alert>
                    </ToggleCard>
                )}
            />
        </AssistantTabForm>
    );
};

export default AssistantKnowledgeTab;
